import { push, replace } from "connected-react-router";
import { isEmpty } from "lodash";
import * as form from "middleware/form";
import * as transfersMiddleware from "middleware/transfers";
import { getStateByCode, transactionStatus } from "pages/transactions/constants";
import { actions as notificationActions } from "reducers/notification";
import { types } from "reducers/transfers";
import { call, put, takeLatest } from "redux-saga/effects";
import { handleError } from "sagas";
import * as i18n from "util/i18n";
import { credentialsErrorMessage } from "util/validateMessage";

const sagas = [
    takeLatest(types.REQUEST_INTERNAL_TRANSFER_DATA, generateInternalTransfer),
    takeLatest(types.REQUEST_OTHER_TRANSFER_DATA, generateOtherTransfer),
    takeLatest(types.REQUEST_ACCOUNT_INFO_BY_CBU, getAccountInfoByCbu),
    takeLatest(types.CANCEL_TRANSFER_REQUEST, cancelTransfer),
    takeLatest(types.GENERATE_TRANSFER_MEP_VALIDATION_REQUEST, generateMepValidation),
    takeLatest(types.GENERATE_TRANSFER_MEP_REQUEST, generatMepTransfer),
    takeLatest(types.GENERATE_TRANSFER_CLOSINGHOUR_VALIDATION_REQUEST, generateTransferClosingHourValidation),
];

export default sagas;

const ticketStates = {
    COR020W: "ERROR",
    COR023I: transactionStatus.PENDING,
    COR000I: transactionStatus.FINISHED,
    COR013I: transactionStatus.SCHEDULED,
    API515I: transactionStatus.PENDING_BACKOFFICE,
};

function* generateInternalTransfer({ internalTransferData, formikBag, credentialType }) {
    try {
        const response = yield call(transfersMiddleware.internalTransfer, internalTransferData);

        if (response.type === "W" && response.data?.code !== "API570W") {
            if (response.data?.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", [
                        "TransferSummaryPage",
                        "InternalTransferTransaction", // scope de comprobante
                    ]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", [
                            "TransferSummaryPage",
                            "InternalTransferTransaction", // scope de comprobante
                        ]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            }
            yield put(
                notificationActions.showNotification(response.data?.message, "error", [
                    "TransferSummaryPage",
                    "InternalTransferTransaction", // scope de comprobante
                ]),
            );
        } else {
            if (response.data.code === "API570W") {
                yield put(
                    notificationActions.showNotification(
                        response.data?.data?.message || i18n.get("global.unexpectedError"),
                        "error",
                        ["TransferSummaryPage", "InternalTransferTransaction"],
                    ),
                );
                return;
            }
            yield put({
                type: types.TRANSFER_TICKET_SUCCESS,
                transferTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            yield put({
                type: types.SET_INTERNAL_TRANSFER_DATA,
                internalTransferData: {},
            });
            yield put({
                type: types.CLEAN_PROGRAM_DATA,
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(i18n.get("transfer.success"), "success", [
                        "InternalTransferTransaction",
                    ]),
                );
            }
        }
    } catch (error) {
        if (error?.code === "ECONNABORTED") {
            const _error = {
                ...error,
                data: {
                    ...error.data,
                    message: i18n.get(
                        "front.duplicate.operation.message",
                        "No pudimos confirmar el resultado de la operación. Te sugerimos revisar tus movimientos antes de intentar nuevamente.",
                    ),
                },
            };
            yield call(handleError, _error);
            return;
        }

        throw new Error(error);
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* generateOtherTransfer({ otherTransferData, formikBag, credentialType }) {
    try {
        const response = yield call(transfersMiddleware.otherTransfer, otherTransferData);
        const defaultMessage = i18n.get("global.unexpectedError");

        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data);

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });

                yield put(
                    notificationActions.showNotification(message, "error", [
                        "TransferSummaryPage",
                        "OtherTransferTransaction", // scope del comprobante
                    ]),
                );

                return;
            }

            if (
                response.data.code === "API570W" ||
                response.data.code === "API571W" ||
                response.data.code === "API572W"
            ) {
                yield put(
                    notificationActions.showNotification(defaultMessage, "error", [
                        "TransferSummaryPage",
                        "OtherTransferTransaction", // scope del comprobante
                    ]),
                );
            } else {
                yield put(
                    notificationActions.showNotification(message || response.data?.message, "error", [
                        "TransferSummaryPage",
                        "OtherTransferTransaction", // scope del comprobante
                    ]),
                );
            }

            return;
        }

        yield put({
            type: types.TRANSFER_TICKET_SUCCESS,
            transferTicket: {
                status: ticketStates[response.data.code],
                ...response.data,
            },
        });

        yield put({
            type: types.SET_OTHER_TRANSFER_DATA,
            otherTransferData: {},
        });

        if (ticketStates[response.data.code] === ticketStates.COR000I) {
            yield put(
                notificationActions.showNotification(i18n.get("transfer.success"), "success", [
                    "OtherTransferTransaction",
                ]),
            );
        }
    } catch (error) {
        // El CBU esta en la lista negra
        if (error?.data?.code === "BAK618E") {
            yield put(push("/transfers/error"));
            return;
        }
        if (error?.code === "ECONNABORTED") {
            const _error = {
                ...error,
                data: {
                    ...error.data,
                    message: i18n.get(
                        "front.duplicate.operation.message",
                        "No pudimos confirmar el resultado de la operación. Te sugerimos revisar tus movimientos antes de intentar nuevamente.",
                    ),
                },
            };
            yield call(handleError, _error);
            return;
        }

        throw new Error(error);
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* getAccountInfoByCbu({ creditCbu, formikBag }) {
    const { setFieldValue } = formikBag;
    try {
        const response = yield call(transfersMiddleware.accountInfoByCbu, { creditCbu });

        if (response.type === "W") {
            const message = credentialsErrorMessage(response.data?.data);

            yield put(
                notificationActions.showNotification(message || response.data?.message, "error", ["otherTransfers"]),
            );
            return;
        }

        const { beneficiary } = response.data.data;

        if (isEmpty(beneficiary)) {
            yield put(
                notificationActions.showNotification("transfers.problems.finding.cbu", "error", ["otherTransfers"]),
            );
            yield put(
                notificationActions.showNotification(i18n.get("mep.problems.finding.cbu"), "error", [
                    "mepNotification",
                ]),
            );
            return;
        }

        setFieldValue("creditCbu", beneficiary.cbu);
        setFieldValue("creditCuit", beneficiary.recipientDocumentNumber);
        setFieldValue("nameRecipient", beneficiary.recipientName);
        setFieldValue("email", beneficiary.recipientEmail);
        setFieldValue("beneficiary", {
            productType: beneficiary.productType,
            localBank: beneficiary.localBank,
            accountNumber: beneficiary.accountNumber,
            recipientName: beneficiary.recipientName,
            recipientDocumentType: beneficiary.recipientDocumentType,
            recipientDocumentNumber: beneficiary.recipientDocumentNumber,
            recipientEmail: beneficiary.recipientEmail,
            cbu: beneficiary.cbu,
            isOwn: beneficiary.isOwn,
        });
        setFieldValue("transferKind", beneficiary.isOwn === "0" ? "OTHER" : "SAME");
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* cancelTransfer({ summary, formikBag, credentialType }) {
    const params = {
        [`_${credentialType}`]: summary[`_${credentialType}`],
    };

    try {
        const response = yield call(form.cancelTransaction, summary.idTransaction, { ...params });
        const { hasValidationError } = getStateByCode(response.data.code);

        if (response.type === "W") {
            if (hasValidationError) {
                const msj = response.data?.data[credentialType];
                if (msj) {
                    yield put(notificationActions.showNotification(msj, "error", ["TransferSummaryPage"]));
                    formikBag.setErrors({
                        credential: msj,
                    });
                    return;
                }
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["TransferSummaryPage"]),
                );
            }
        } else {
            if (response.data.code === "API524W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["TransferSummaryPage"]),
                );
                return;
            }

            yield call(
                form.updateUserEditor,
                summary.idTransaction,
                summary.userId,
                "CANCELLED",
                null,
                summary.cancelTransaction,
            );

            yield put({
                type: types.TRANSFER_TICKET_SUCCESS,
                transferTicket: {
                    status: "CANCELLED",
                    ...response.data,
                    idTransaction: summary.idTransaction,
                },
            });

            yield put({
                type: types.SET_OTHER_TRANSFER_DATA,
                otherTransferData: {},
            });

            yield put({
                type: types.SET_INTERNAL_TRANSFER_DATA,
                internalTransferData: {},
            });
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* generateMepValidation() {
    const response = yield call(transfersMiddleware.mepTransferValidation);
    if (response.type === "W") {
        const allowedMep = response.data?.data?.allowedMep === "true";
        yield put({ type: types.GENERATE_TRANSFER_MEP_VALIDATION_FAILURE, allowedMep });
        yield put(notificationActions.showNotification(response.data?.data?.message, "warning", ["mepNotification"]));
    } else {
        yield put({
            type: types.GENERATE_TRANSFER_MEP_VALIDATION_SUCCESS,
        });
    }
}

function* generatMepTransfer({ otherTransferData, formikBag, credentialType }) {
    try {
        const response = yield call(transfersMiddleware.mepTransfer, otherTransferData);
        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data);

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });

                yield put(notificationActions.showNotification(message, "error", ["MepTransferSummaryPage"]));

                return;
            }

            yield put(
                notificationActions.showNotification(i18n.get("generate.mep.general.error"), "error", [
                    "MepTransferSummaryPage",
                ]),
            );
        } else {
            yield put({
                type: types.GENERATE_TRANSFER_MEP_SUCCESS,
            });
            yield put(replace(`/mep/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* generateTransferClosingHourValidation() {
    const response = yield call(transfersMiddleware.transferClosingHourValidation);
    if (response.type === "W") {
        yield put({
            type: types.GENERATE_TRANSFER_CLOSINGHOUR_VALIDATION_FAILURE,
        });
        yield put(
            notificationActions.showNotification(response.data?.data?.message, "warning", [
                "otherTransfers",
                "newLotPaymentPage",
            ]),
        );
    } else {
        yield put({
            type: types.GENERATE_TRANSFER_CLOSINGHOUR_VALIDATION_SUCCESS,
        });
    }
}
